import flatpickr from 'flatpickr';
import ensureDhdsMonthYearHeader from './flatpickrDhdsHeader';

const DEFAULT_PLACEHOLDER = 'DD / MM / YYYY';

function normalisePlaceholder(placeholder) {
  if (!placeholder) return '';
  return String(placeholder).trim().toUpperCase().replace(/\s+/g, '').replace(/[.-]/g, '/').replace(/\/+/g, '/');
}

function inferDateFormatFromPlaceholder(placeholder) {
  const text = String(placeholder || '').trim();
  if (!text) return 'd / m / Y';

  let separator = '/';
  if (text.includes('-')) separator = '-';
  else if (text.includes('.')) separator = '.';
  else if (text.includes('/')) separator = '/';

  const includeSpaces = /\s/.test(text);
  return includeSpaces ? `d ${separator} m ${separator} Y` : `d${separator}m${separator}Y`;
}

function buildLabelForMap(doc) {
  const map = new Map();
  if (!doc || typeof doc.querySelectorAll !== 'function') return map;

  Array.from(doc.querySelectorAll('label[for]')).forEach((label) => {
    const key = label && label.htmlFor;
    if (!key) return;
    const value = (label.textContent || '').trim();
    if (value) map.set(key, value);
  });

  return map;
}

function dispatchDateInputEvents(input) {
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

function isDobLikeInput(input, labelForMap) {
  if (!input) return false;

  // Explicit opt-in for server-rendered (Razor) or legacy markup
  if (input.getAttribute('data-flatpickr') === 'date') return true;

  const placeholder = input.getAttribute('placeholder') || '';
  const normalisedPlaceholder = normalisePlaceholder(placeholder);

  // Common placeholder variants
  if (normalisedPlaceholder === 'DD/MM/YYYY') return true;

  const id = input.getAttribute('id') || '';
  const name = input.getAttribute('name') || '';
  const combined = `${id} ${name}`.toLowerCase();

  // Common id/name variants
  if (combined.includes('dob')) return true;
  if (combined.includes('dateofbirth') || combined.includes('date-of-birth')) return true;

  // Labels like "Date of birth" / "DOB"
  if (id && labelForMap && labelForMap.has(id)) {
    const labelText = String(labelForMap.get(id)).toLowerCase();
    if (labelText.includes('date of birth') || labelText === 'dob' || labelText.includes('dob')) return true;
  }

  return false;
}

/**
 * Attach Flatpickr to any date input using the legacy placeholder format.
 *
 * This intentionally targets plain inputs (including those rendered by Razor)
 * as well as older Vue templates that aren't using the shared FormInput.
 */
export default function initFlatpickrDateInputs(root = document) {
  if (typeof window === 'undefined') return [];
  if (!root || typeof root.querySelectorAll !== 'function') return [];

  const doc = root.nodeType === 9 ? root : root.ownerDocument || document;
  const labelForMap = buildLabelForMap(doc);

  const allTextInputs = Array.from(root.querySelectorAll('input[type="text"], input[type="tel"]'));
  const candidates = allTextInputs.filter((input) => {
    return input && !input.disabled && !input.hasAttribute('data-flatpickr-owned') && !input.hasAttribute('data-no-flatpickr') && isDobLikeInput(input, labelForMap);
  });

  candidates.forEach((input) => {
    if (!input) return;

    // Prevent double init (Flatpickr marks the element)
    if (input.classList && input.classList.contains('flatpickr-input')) return;

    // Fallback idempotency marker
    if (input.dataset && input.dataset.flatpickrInitialised === 'true') return;

    try {
      const placeholder = input.getAttribute('placeholder') || DEFAULT_PLACEHOLDER;
      flatpickr(input, {
        disableMobile: true,
        allowInput: true,
        dateFormat: inferDateFormatFromPlaceholder(placeholder),
        monthSelectorType: 'static',
        onChange: () => {
          dispatchDateInputEvents(input);
        },
        onClose: () => {
          dispatchDateInputEvents(input);
        },
        onReady: (_selectedDates, _dateStr, instance) => {
          ensureDhdsMonthYearHeader(instance);
        },
        onOpen: (_selectedDates, _dateStr, instance) => {
          ensureDhdsMonthYearHeader(instance);
        },
        onMonthChange: (_selectedDates, _dateStr, instance) => {
          ensureDhdsMonthYearHeader(instance);
        },
        onYearChange: (_selectedDates, _dateStr, instance) => {
          ensureDhdsMonthYearHeader(instance);
        },
      });

      if (input.dataset) input.dataset.flatpickrInitialised = 'true';

      // Used by SCSS to render a calendar icon inside the input.
      input.setAttribute('data-dhds-calendar-icon', 'true');

      // Prevent browser autocomplete fighting the picker
      if (!input.getAttribute('autocomplete')) input.setAttribute('autocomplete', 'off');
    } catch (e) {
      // No-op: failing to attach a picker should not break the page
    }
  });

  return candidates;
}
